interface TrackingLegsUnit<V, D> {
    vehicle?: V;
    driver?: D;
}

interface TrackingLegsTrip<V, D> extends TrackingLegsUnit<V, D> {
    originalUnit?: TrackingLegsUnit<V, D>;
    checkpoints?: {
        unitChange?: TrackingLegsUnit<V, D> & { handover?: { completedAt?: string; incomingArrivedAt?: string } };
    }[];
}

/** Whose device answers for a stretch of the trip, and for which window. A trip without a desenganche is a single leg. */
export type TrackingLeg<V, D> = TrackingLegsUnit<V, D> & { from: string; to: string };

/**
 * The trip as one leg, or one per unit that drove it when it changed hands along the way.
 *
 * Vehicle and driver are always explicit on a unit change, so the units read off the handovers
 * directly — no need to fold carriers or semitrailers, which tracking does not care about. Falls
 * back to the single leg whenever the trip cannot be split, so nothing but a desenganche changes.
 *
 * Dates must be ISO strings: they are compared lexicographically.
 */
export const getTrackingLegs = <V, D>(trip: TrackingLegsTrip<V, D>, from: string, to: string): TrackingLeg<V, D>[] => {
    const wholeTrip = [{ vehicle: trip.vehicle, driver: trip.driver, from, to }];
    const handovers = (trip.checkpoints ?? []).flatMap((checkpoint) => {
        const unitChange = checkpoint?.unitChange;
        const completedAt = unitChange?.handover?.completedAt;
        if (!unitChange || !completedAt || !(unitChange.vehicle || unitChange.driver)) return [];

        return [
            {
                vehicle: unitChange.vehicle,
                driver: unitChange.driver,
                at: unitChange.handover?.incomingArrivedAt ?? completedAt,
            },
        ];
    });

    // Without the original unit the root is already the last one, so section 1 cannot be recovered
    const sectionOne = trip.originalUnit?.vehicle || trip.originalUnit?.driver ? trip.originalUnit : undefined;
    if (!handovers.length || !sectionOne) return wholeTrip;

    // A handover can be stamped outside the trip's own window — clamping keeps every leg ordered
    // and inside it, so Dolphin is never asked for a range that runs backwards
    const clamp = (at: string) => (at < from ? from : at > to ? to : at);

    return [sectionOne, ...handovers].map((unit, index) => ({
        vehicle: unit.vehicle,
        driver: unit.driver,
        from: index === 0 ? from : clamp(handovers[index - 1].at),
        to: index === handovers.length ? to : clamp(handovers[index].at),
    }));
};
